//@ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { useRecordContext } from 'react-admin';
import {
	Card,
	CardContent,
	Typography,
	Stack,
	Button,
	Box,
	Divider,
	Alert,
	CircularProgress,
	List,
	ListItem,
	ListItemText,
	ListItemSecondaryAction,
	IconButton,
	Collapse,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Switch,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import r from '../../Utils/fetch';

const w = window as any;

interface DatabaseMetaData {
	name: string;
	description: string;
	type: string;
	isMetric: boolean;
	quantiId?: boolean;
}

interface OrderedField {
	fieldId: string;
	databaseMetaData: DatabaseMetaData;
}

// Prebuild as served by api-connectors-v2, read from the connectors submodule.
interface Prebuild {
	id: string;
	name: string;
	description?: string;
	schema?: {
		tableName: string;
		orderedFields?: OrderedField[];
	};
}

// #region TableItem
const TableItem = ({ prebuild }: { prebuild: Prebuild }) => {
	const [expanded, setExpanded] = useState(false);
	const fields = prebuild.schema?.orderedFields || [];
	const metrics = fields.filter((f) => f.databaseMetaData.isMetric);
	const dimensions = fields.filter((f) => !f.databaseMetaData.isMetric);

	return (
		<>
			<ListItem
				sx={{
					bgcolor: 'background.paper',
					mb: 1,
					borderRadius: 1,
					border: '1px solid',
					borderColor: 'divider',
				}}
			>
				<ListItemText
					primary={
						<Stack direction="row" spacing={1} alignItems="center">
							<Typography variant="subtitle1">{prebuild.name}</Typography>
							<Chip label={prebuild.id} size="small" variant="outlined" />
						</Stack>
					}
					secondary={
						<>
							<Typography variant="body2" color="textSecondary">
								{prebuild.description || 'Pas de description'}
							</Typography>
							<Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
								<Chip label={`${dimensions.length} dimensions`} size="small" color="default" />
								<Chip label={`${metrics.length} metrics`} size="small" color="primary" />
							</Stack>
						</>
					}
				/>
				<ListItemSecondaryAction>
					<IconButton onClick={() => setExpanded(!expanded)} size="small">
						{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</IconButton>
				</ListItemSecondaryAction>
			</ListItem>
			<Collapse in={expanded} timeout="auto" unmountOnExit>
				<Box sx={{ pl: 2, pr: 2, pb: 2 }}>
					<Typography variant="caption" color="textSecondary">
						Colonnes ({fields.length})
					</Typography>
					<TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
						<Table size="small">
							<TableHead>
								<TableRow sx={{ bgcolor: 'grey.50' }}>
									<TableCell width={60} align="center">quantiId</TableCell>
									<TableCell>name</TableCell>
									<TableCell>description</TableCell>
									<TableCell width={100}>type</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{fields.map((field) => (
									<TableRow key={field.fieldId} hover>
										<TableCell align="center">
											<Switch checked={!!field.databaseMetaData.quantiId} size="small" disabled />
										</TableCell>
										<TableCell>
											<Typography variant="body2">{field.databaseMetaData.name}</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="textSecondary" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
												{field.databaseMetaData.description || '-'}
											</Typography>
										</TableCell>
										<TableCell>
											<Chip
												label={field.databaseMetaData.type}
												size="small"
												variant="outlined"
												sx={{ fontSize: '0.75rem' }}
											/>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
			</Collapse>
		</>
	);
};

// #region ConnectorConfigPrebuilds
// Read-only: prebuilds are edited in the connectors repository, not from the admin.
export const ConnectorConfigPrebuilds = () => {
	const record = useRecordContext();

	const [prebuilds, setPrebuilds] = useState<Prebuild[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// #region fetchPrebuilds
	const fetchPrebuilds = useCallback(async () => {
		if (!record?.sku) return;

		setLoading(true);
		setError(null);

		try {
			const response = await r<any>({
				url: `${w.API_CONNECTORS_CONFIG_URL}/v1/connectors/${record.sku}/prebuilds`,
				method: 'GET',
			});
			setPrebuilds(response.prebuilds || []);
		} catch (err: any) {
			setError(err.message || 'Erreur lors du chargement des prebuilds');
		} finally {
			setLoading(false);
		}
	}, [record?.sku]);

	useEffect(() => {
		fetchPrebuilds();
	}, [fetchPrebuilds]);

	if (!record) return null;

	return (
		<Card>
			<CardContent>
				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
					<Typography variant="h6">Prebuilds</Typography>
					<Button startIcon={<RefreshIcon />} onClick={fetchPrebuilds} disabled={loading} size="small">
						Rafraîchir
					</Button>
				</Stack>

				<Divider sx={{ mb: 2 }} />

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				{loading && (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
						<CircularProgress />
					</Box>
				)}

				{!loading && prebuilds.length > 0 && (
					<>
						<Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
							{prebuilds.length} table(s) préconfigurée(s)
						</Typography>
						<List disablePadding>
							{prebuilds.map((prebuild) => (
								<TableItem key={prebuild.id} prebuild={prebuild} />
							))}
						</List>
					</>
				)}

				{!loading && !error && prebuilds.length === 0 && (
					<Alert severity="info">Aucun prebuild pour ce connecteur.</Alert>
				)}
			</CardContent>
		</Card>
	);
};
